import { resolveLocalizedText } from '@seaa/shared/localized-text'
import { type Locale, type PublicSiteContext } from '@seaa/shared'

/** Resolve merchant-authored copy once for page text, metadata and service links. */
export function localizePublicSite(site: PublicSiteContext, locale: Locale): PublicSiteContext {
  const copy = (values: Parameters<typeof resolveLocalizedText>[0], fallback: string | null) => resolveLocalizedText(values, locale, fallback ?? '')
  return {
    ...site,
    publicTitle: copy(site.publicTitleLocales, site.publicTitle),
    publicTagline: copy(site.publicTaglineLocales, site.publicTagline),
    publicDescription: copy(site.publicDescriptionLocales, site.publicDescription),
    publicAbout: copy(site.publicAboutLocales, site.publicAbout),
    linkPageTitle: copy(site.linkPageTitleLocales, site.linkPageTitle),
    linkPageBio: copy(site.linkPageBioLocales, site.linkPageBio),
    ...(site.linkBookingServices ? { linkBookingServices: site.linkBookingServices.map((service) => ({ ...service, name: copy(service.nameLocales, service.name) })) } : {}),
    services: site.services.map((service) => ({ ...service, name: copy(service.nameLocales, service.name), description: copy(service.descriptionLocales, service.description) })),
    resources: site.resources.map((resource) => ({ ...resource, name: copy(resource.nameLocales, resource.name), description: copy(resource.descriptionLocales, resource.description ?? null) })),
    testimonials: site.testimonials.map((entry) => ({ ...entry, resourceName: copy(entry.resourceNameLocales, entry.resourceName), serviceName: copy(site.services.find((service) => service.id === entry.serviceId)?.nameLocales, entry.serviceName) })),
  }
}

export function publicContact(site: PublicSiteContext) {
  const whatsapp = site.publicLinks.find((link) => link.kind === 'whatsapp')
  const directionsUrl = site.publicLinks.find((link) => link.kind === 'directions')?.url
    || (site.publicAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([site.publicAddress, site.city].filter(Boolean).join(', '))}` : null)
  const contact = whatsapp ? { kind: 'whatsapp' as const, url: whatsapp.url }
    : site.publicPhone ? { kind: 'phone' as const, url: `tel:${site.publicPhone}` }
      : site.publicEmail ? { kind: 'email' as const, url: `mailto:${site.publicEmail}` } : null
  return { directionsUrl, contact, secondaryLinks: site.publicLinks.filter((link) => link.url !== directionsUrl && link.url !== contact?.url) }
}

/** A locale may have no reviews; preserve genuine reviews and validate stale filters. */
export function selectPublicTestimonials(site: PublicSiteContext, locale: Locale, requestedTarget: string) {
  const localized = site.testimonials.filter((entry) => entry.locale === locale)
  const pool = localized.length ? localized : site.testimonials
  const services = Array.from(new Map(pool.filter((entry) => entry.serviceId && entry.serviceName)
    .map((entry) => [`service:${entry.serviceId}`, { id: `service:${entry.serviceId}`, name: entry.serviceName! }])).values())
  const resources = Array.from(new Map(pool.map((entry) => [`resource:${entry.resourceId}`, { id: `resource:${entry.resourceId}`, name: entry.resourceName }])).values())
  const targets = [...services, ...resources]
  const activeTarget = targets.some((target) => target.id === requestedTarget) ? requestedTarget : 'all'
  const entries = activeTarget === 'all' ? pool : activeTarget.startsWith('service:')
    ? pool.filter((entry) => entry.serviceId === activeTarget.slice(8))
    : pool.filter((entry) => entry.resourceId === activeTarget.slice(9))
  return { pool, targets, activeTarget, entries }
}
